/**
 * @fileoverview Resolve the display identity for a signed-in user.
 *
 * WHAT THIS FILE DOES
 * - `resolveDisplayIdentity` returns the best email to show in the UI,
 *   consulting every signed-in source: HttpOnly cookie session, Firebase
 *   `User`, extension bridge state, and the `/api/profile` document.
 * - `resolveUserEmail` / `resolveUserEmailWithReload` are the Firebase-only
 *   path retained for sign-in side-effects (e.g. `sendAuthImport`) that have
 *   nothing but a Firebase `User` in hand.
 *
 * INVARIANT — NEVER RETURN A UID
 * - These functions return `string | null`. They never return a Firebase UID
 *   or any other opaque identifier. Callers that need a UI fallback should
 *   render `result ?? 'Signed in'` (or hide the row entirely). The bug class
 *   this module exists to kill is `resolvedEmail ?? user.uid`.
 *
 * WHY THIS FILE EXISTS
 * - Every settings/profile/admin/join surface used to reinvent the same
 *   `?? ?? ?? fallback` ladder. They drifted: `/join` consulted server +
 *   bridge + Firebase, `/options` only consulted Firebase, `/admin` and the
 *   extension options leaked the UID into the UI when the Firebase user had
 *   no email. Centralising the resolver removes the drift.
 *
 * ARCHITECTURE ROLE
 * - Part of @gist-protocol/core shared utilities.
 * - Zero dependencies — duck-types every source so this module stays runnable
 *   in workers, content scripts, the backend, and tests without importing
 *   the Firebase SDK.
 *
 * USAGE
 *   import {
 *     resolveDisplayIdentity,
 *     resolveUserEmail,
 *     resolveUserEmailWithReload
 *   } from '@gist-protocol/core';
 *
 *   const email = resolveDisplayIdentity({
 *     serverIdentity,   // HttpOnly cookie session  { email?, uid? }
 *     firebaseUser,     // Firebase `User`          { email?, providerData? }
 *     bridgeState,      // extension auth bridge    { email?, profile? }
 *     profile           // /api/profile response    { email? }
 *   });                                                         // string|null
 *
 *   const email = resolveUserEmail(firebaseUser);               // sync, pure
 *   const email = await resolveUserEmailWithReload(user);       // hydrates if null
 */

/**
 * @typedef {Object} UserEmailLike
 * @property {string|null|undefined} [email]
 * @property {Array<{ email?: string|null }>|null|undefined} [providerData]
 * @property {() => Promise<void>} [reload]
 */

/**
 * @typedef {Object} IdentityRecord
 * @property {string|null|undefined} [email]
 */

/**
 * @typedef {Object} BridgeStateLike
 * @property {string|null|undefined} [email]
 * @property {IdentityRecord|null|undefined} [profile]
 */

/**
 * @typedef {Object} DisplayIdentitySources
 * @property {IdentityRecord|null|undefined} [serverIdentity] HttpOnly cookie session.
 * @property {UserEmailLike|null|undefined} [firebaseUser] Firebase `User`.
 * @property {BridgeStateLike|null|undefined} [bridgeState] Extension auth bridge state.
 * @property {IdentityRecord|null|undefined} [profile] `/api/profile` response.
 */

function nonEmpty(value) {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Resolve the best-known email for a Firebase-like user object.
 *
 * Scans `user.email` first, then EVERY entry in `providerData` (not just
 * index 0 — federated providers do not always land at the top of the array).
 *
 * @param {UserEmailLike|null|undefined} user
 * @returns {string|null} The resolved email, or `null` if none is available.
 */
export function resolveUserEmail(user) {
  if (!user) return null;
  const direct = nonEmpty(user.email);
  if (direct) return direct;
  const providers = Array.isArray(user.providerData) ? user.providerData : [];
  for (const provider of providers) {
    const candidate = provider ? nonEmpty(provider.email) : null;
    if (candidate) return candidate;
  }
  return null;
}

/**
 * Resolve the display identity for a signed-in user from every available
 * source. Returns `null` when no source carries an email — callers should
 * fall back to a human string like `'Signed in'`, never to a UID.
 *
 * Priority (first non-empty wins):
 *   1. `serverIdentity.email`   — HttpOnly cookie session (most authoritative;
 *      it is what same-origin APIs authenticate as).
 *   2. `firebaseUser.email` and `firebaseUser.providerData[*].email`
 *      (every provider, not just index 0).
 *   3. `bridgeState.email`      — extension auth bridge (already merges its
 *      own profile email; see `web-auth-bridge.ts`).
 *   4. `bridgeState.profile.email`
 *   5. `profile.email`          — `/api/profile` response.
 *
 * @param {DisplayIdentitySources|null|undefined} sources
 * @returns {string|null}
 */
export function resolveDisplayIdentity(sources) {
  if (!sources) return null;

  const fromServer = sources.serverIdentity ? nonEmpty(sources.serverIdentity.email) : null;
  if (fromServer) return fromServer;

  const fromFirebase = resolveUserEmail(sources.firebaseUser);
  if (fromFirebase) return fromFirebase;

  const bridge = sources.bridgeState ?? null;
  const fromBridge = bridge ? nonEmpty(bridge.email) : null;
  if (fromBridge) return fromBridge;
  const fromBridgeProfile = bridge?.profile ? nonEmpty(bridge.profile.email) : null;
  if (fromBridgeProfile) return fromBridgeProfile;

  const fromProfile = sources.profile ? nonEmpty(sources.profile.email) : null;
  if (fromProfile) return fromProfile;

  return null;
}

/**
 * Resolve the user's email, calling `user.reload()` once if it is missing.
 *
 * Useful immediately after sign-in flows where the cached `User` object has
 * not yet hydrated provider data. Reload errors are swallowed — callers get
 * `null` instead of an exception when the network/refresh fails.
 *
 * @param {UserEmailLike|null|undefined} user
 * @returns {Promise<string|null>}
 */
export async function resolveUserEmailWithReload(user) {
  const direct = resolveUserEmail(user);
  if (direct) return direct;
  if (user && typeof user.reload === 'function') {
    try {
      await user.reload();
    } catch {
      // Ignore — fall through to a second resolution attempt.
    }
    return resolveUserEmail(user);
  }
  return null;
}
